import { loyaltyProgramLevelsList } from 'components/ReusableComponents/Arrivals/LoyaltyIcon/loyaltyLevels'
import { SecondarySmallButton } from 'components/ReusableComponents/Button/SmallButtons'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import InfoIcon from 'components/ReusableComponents/Icons/Info'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import Table, { Cell, TitleCell, TitleRow } from 'components/ReusableComponents/Table'
import FilterSidebar from 'features/CustomCampaigns/FilterSidebar'
import { isProtectedDefaultAudience } from 'features/CustomCampaigns/campaignAudienceDefaults'
import SearchIcon from 'features/CustomCampaigns/FilterSidebar/SearchIcon'
import RateCodeFilter from 'features/CustomCampaigns/RateCodeFilter'
import usePlausible from 'hooks/usePlausible'
import moment from 'moment'
import { startTransition, useEffect, useReducer, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Redirect, useHistory, useParams } from 'react-router'
import { Link } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'
import ReactTooltip from 'react-tooltip'
import audienceApi from 'services/api/audience'
import campaignApi from 'services/api/campaign'
import { formatNumberAsThousands } from 'utils/priceHelpers'

import styles from './Audience.module.scss'
import { ReactComponent as DeleteIcon } from './delete.svg'

/** If the audience API response takes longer than this, skip the guest preview table (performance). */
const AUDIENCE_PREVIEW_MAX_FETCH_MS = 5000

const initialState = {
  minStays: 0,
  maxStays: 6,
  distanceFromHotel: null,
  bookingSource: null, // "ota", "direct", null,
  membership: null, // "level1, "level2", null,
  nightlyRate: null, // "0-100", "0-200", null
  rateCodes: null,
  checkoutFrom: null,
  checkoutTo: null,
  hasUnsavedChanges: false,
  isSaving: false,
  reviewRating: null,
  futureStayDateTo: null,
  futureStayDateFrom: null
}

export const audienceFilterTypes = {
  gotConfig: 'gotConfig',
  saveConfig: 'saveConfig',
  savingConfig: 'savingConfig',
  numberOfStays: 'numberOfStays',
  distanceFromHotel: 'distanceFromHotel',
  bookingSource: 'bookingSource',
  membership: 'membership',
  nightlyRate: 'nightlyRate',
  rateCodes: 'rateCodes',
  checkoutFrom: 'checkoutFrom',
  checkoutTo: 'checkoutTo',
  futureStayDateFrom: 'futureStayDateFrom',
  futureStayDateTo: 'futureStayDateTo',
  reviewRating: 'reviewRating',
  states: 'states'
}

const filterReducer = (state, action) => {
  switch (action.type) {
    case audienceFilterTypes.gotConfig:
      return {
        ...state,
        rateCodes: action.payload.rateCodes ? action.payload.rateCodes.split(',') : [],
        minStays: action.payload.minStays,
        maxStays: action.payload.maxStays,
        distanceFromHotel: action.payload.distance,
        checkoutFrom: action.payload.lastUseFrom,
        checkoutTo: action.payload.lastUseTo,
        futureStayDateFrom: action.payload.futureStayDateFrom,
        futureStayDateTo: action.payload.futureStayDateTo,
        bookingSource: action.payload.bookingSource,
        membership: action.payload.membership,
        nightlyRate: action.payload.nightlyRate,
        reviewRating: action.payload.reviewRating,
        states: action.payload.states ? action.payload.states.split(',') : []
      }
    case audienceFilterTypes.saveConfig:
      return { ...state, hasUnsavedChanges: false, isSaving: false }
    case audienceFilterTypes.savingConfig:
      return { ...state, isSaving: true }
    case audienceFilterTypes.numberOfStays:
      return {
        ...state,
        minStays: action.payload.minStays,
        maxStays: action.payload.maxStays,
        hasUnsavedChanges: true
      }
    case audienceFilterTypes.distanceFromHotel:
      if (action.payload === state.distanceFromHotel) {
        return { ...state }
      }

      return { ...state, distanceFromHotel: action.payload, hasUnsavedChanges: true }
    case audienceFilterTypes.rateCodes:
      return {
        ...state,
        rateCodes: action.payload,
        hasUnsavedChanges: true
      }
    case audienceFilterTypes.checkoutFrom:
      return { ...state, checkoutFrom: action.payload, hasUnsavedChanges: true }
    case audienceFilterTypes.checkoutTo:
      return { ...state, checkoutTo: action.payload, hasUnsavedChanges: true }

    case audienceFilterTypes.futureStayDateFrom:
      return { ...state, futureStayDateFrom: action.payload, hasUnsavedChanges: true }

    case audienceFilterTypes.futureStayDateTo:
      return { ...state, futureStayDateTo: action.payload, hasUnsavedChanges: true }

    case audienceFilterTypes.bookingSource:
      return { ...state, bookingSource: action.payload, hasUnsavedChanges: true }
    case audienceFilterTypes.membership:
      if (action.payload) {
        return { ...state, membership: action.payload, hasUnsavedChanges: true }
      } else {
        return { ...state }
      }

    case audienceFilterTypes.reviewRating:
      if (action.payload) {
        return { ...state, reviewRating: action.payload, hasUnsavedChanges: true }
      } else {
        return { ...state }
      }

    case audienceFilterTypes.nightlyRate:
      return { ...state, nightlyRate: action.payload, hasUnsavedChanges: true }

    case audienceFilterTypes.states:
      return {
        ...state,
        states: action.payload,
        hasUnsavedChanges: true,
        distanceFromHotel: action.payload.length > 0 ? null : state.distanceFromHotel
      }

    default:
      return state
  }
}

const Audience = () => {
  const [isShowingRateCodes, setIsShowingRateCodes] = useState(false)
  const [audianceName, setAudianceName] = useState()
  const [error, setError] = useState()
  const [preview, setPreview] = useState()
  const [deferredPreview, setDeferredPreview] = useState(null)
  const [size, setSize] = useState(0)
  const [totalSize, setTotalSize] = useState(0)
  const [emailSize, setEmailSize] = useState(0)
  const [textSize, setTextSize] = useState(0)

  const [isLoading, setIsLoading] = useState(false)
  const [isProtected, setIsProtected] = useState(false)

  const { id } = useParams()

  const [saveId, setSaveId] = useState(1)

  const [filterState, dispatchFilterState] = useReducer(filterReducer, initialState)

  const [isCreating, setIsCreating] = useState()

  const { addToast } = useToasts()

  const history = useHistory()

  const isFirstRender = useRef(true)
  const audienceFetchStartedAtRef = useRef(null)

  const parentChain = useSelector(s => s.hotelDetails.parentChainCode)

  const loyaltyProgramLevels = loyaltyProgramLevelsList[parentChain] && [
    ...loyaltyProgramLevelsList[parentChain],
    'NoLevel'
  ]

  useEffect(() => {
    if (!audianceName) {
      audienceFetchStartedAtRef.current = Date.now()
      setIsLoading(true)
      audienceApi
        .get(id)
        .then(res => {
          const elapsed = Date.now() - audienceFetchStartedAtRef.current
          const allowPreview =
            res.preview && elapsed <= AUDIENCE_PREVIEW_MAX_FETCH_MS
          dispatchFilterState({ type: audienceFilterTypes.gotConfig, payload: res })
          setAudianceName(res.name)
          setIsProtected(isProtectedDefaultAudience(res))
          setPreview(allowPreview ? res.preview : null)
          setSize(res.size)
          setEmailSize(res.emailSize)
          setTextSize(res.textSize)
          setTotalSize(res.totalSize)
          setError(null)
        })
        .catch(err => {
          setError(err)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [id, audianceName])

  useEffect(() => {
    if (saveId > 1) {
      const fetchStartedAt = Date.now()
      setIsLoading(true)
      audienceApi
        .get(id)
        .then(res => {
          const elapsed = Date.now() - fetchStartedAt
          const allowPreview =
            res.preview && elapsed <= AUDIENCE_PREVIEW_MAX_FETCH_MS
          setPreview(allowPreview ? res.preview : null)
          setSize(res.size)
          setEmailSize(res.emailSize)
          setTextSize(res.textSize)
          setTotalSize(res.totalSize)
          setError(null)
        })
        .catch(err => {
          setError(err)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [id, saveId])

  useEffect(() => {
    if (!preview) {
      setDeferredPreview(null)
      return
    }

    let cancelled = false

    const applyDeferred = () => {
      if (!cancelled) {
        startTransition(() => setDeferredPreview(preview))
      }
    }

    let idleId
    let scheduledWithIdleCallback = false
    if (typeof window !== 'undefined' && window.requestIdleCallback) {
      idleId = window.requestIdleCallback(applyDeferred, { timeout: 2000 })
      scheduledWithIdleCallback = true
    } else {
      idleId = setTimeout(applyDeferred, 0)
    }

    return () => {
      cancelled = true
      if (scheduledWithIdleCallback && typeof window !== 'undefined' && window.cancelIdleCallback) {
        window.cancelIdleCallback(idleId)
      } else {
        clearTimeout(idleId)
      }
    }
  }, [preview])

  function handleSaveRateCodes(rateCodes) {
    setIsShowingRateCodes(false)
    isFirstRender.current = false
    dispatchFilterState({
      type: audienceFilterTypes.rateCodes,
      payload: Object.keys(rateCodes)
    })
  }

  const { postEvent, plausibleEvents } = usePlausible()

  function saveAudience() {
    if (isProtected) {
      return
    }

    postEvent(plausibleEvents.UpdateAudience, {
      id
    })

    const {
      rateCodes,
      minStays,
      maxStays,
      distanceFromHotel,
      checkoutTo,
      checkoutFrom,
      membership,
      bookingSource,
      nightlyRate,
      reviewRating,
      states,
      futureStayDateFrom,
      futureStayDateTo
    } = filterState

    if (
      moment().isAfter(moment(futureStayDateFrom)) ||
      moment().isAfter(moment(futureStayDateTo))
    ) {
      addToast('Future stay dates cannot be in the past', {
        appearance: 'error'
      })
      return
    }

    dispatchFilterState({ type: audienceFilterTypes.savingConfig })

    const membershipList = membership || []
    const hasAllSeleced =
      loyaltyProgramLevels && membershipList.length === loyaltyProgramLevels.length

    audienceApi
      .update({
        id: parseInt(id),
        name: audianceName,
        rateCodes: rateCodes ? rateCodes.join(',') : null,
        states: states ? states.join(',') : null,
        minStays,
        maxStays,
        membership: hasAllSeleced ? null : membership,
        bookingSource: bookingSource,
        nightlyRate: nightlyRate,
        distance: distanceFromHotel,
        lastUseTo: checkoutTo,
        lastUseFrom: checkoutFrom,
        reviewRating: reviewRating,
        futureStayDateFrom,
        futureStayDateTo,
        isForFutureGuests: futureStayDateFrom && futureStayDateTo ? true : false
      })
      .then(() => {
        setSaveId(i => i + 1)
      })
      .then(() => {
        dispatchFilterState({ type: audienceFilterTypes.saveConfig })
      })
      .catch(err => {
        setError(err)
      })
  }

  function createCampaign() {
    setIsCreating(true)

    campaignApi
      .addCustomCampaign({ audienceId: id })
      .then(res => {
        history.push(`/custom-campaigns/${res.item.contentId}`, {
          campaign: res.item
        })
      })
      .catch(err => {
        addToast(`${err}`, {
          appearance: 'error'
        })
      })
      .finally(() => {
        setIsCreating(false)
      })
  }

  const [isDeleted, setIsDeleted] = useState()
  const [isDeletingConfVisible, setIsDeletingConfVisible] = useState(false)

  function deleteAudience() {
    if (isProtected) {
      return
    }

    audienceApi
      .del(parseInt(id))
      .then(() => {
        setIsDeletingConfVisible(false)
        setIsDeleted(true)
      })
      .catch(err => {
        setIsDeletingConfVisible(false)
        setError(err)
      })
  }

  function changeFilterState(action) {
    isFirstRender.current = false
    dispatchFilterState(action)
  }

  const [isRenameDialogVisible, setIsRenameDialogVisible] = useState(false)

  function renameAudience() {
    setIsRenameDialogVisible(false)
  }

  if (error) {
    return <Redirect to="/custom-campaigns" />
  }

  if (isRenameDialogVisible) {
    return (
      <ConfirmationDialog
        title="Rename audience"
        onConfirm={renameAudience}
        positiveMessage="Save"
        onReject={() => setIsRenameDialogVisible(false)}
      >
        <TextInput
          label="Audience name"
          autoFocus
          width="100%"
          className={styles.renameInputHolder}
          value={audianceName}
          onChange={e => setAudianceName(e.target.value)}
        />
      </ConfirmationDialog>
    )
  }

  if (isDeletingConfVisible) {
    return (
      <ConfirmationDialog
        title={`Do you want to delete "${audianceName}" audience`}
        positiveMessage="Yes"
        negativeMessage="No"
        hasDangerousAction
        onConfirm={deleteAudience}
        onClose={() => setIsDeletingConfVisible(false)}
        onReject={() => setIsDeletingConfVisible(false)}
      >
        <p style={{ opacity: 0.8 }}>
          Deleting audiences doesn't effect campaigns already started.
        </p>
      </ConfirmationDialog>
    )
  }

  if (isDeleted) {
    return <Redirect to="/custom-campaigns" />
  }

  if (filterState.rateCodes) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.content}>
          <RateCodeFilter
            isVisible={isShowingRateCodes}
            handleCancel={() => setIsShowingRateCodes(false)}
            handleSave={handleSaveRateCodes}
            savedCodes={filterState.rateCodes}
          />

          <CustomHeader
            title={`${isProtected ? 'Viewing' : 'Editing'} "${audianceName}" audience`}
            width={1080}
          >
            {!isProtected && (
              <SecondarySmallButton
                style={{ minWidth: 80, marginLeft: 12 }}
                onClick={() => setIsRenameDialogVisible(true)}
              >
                Rename
              </SecondarySmallButton>
            )}
          </CustomHeader>
          <FilterSidebar
            handleClickFilterButton={() => setIsShowingRateCodes(true)}
            filterState={filterState}
            dispatchFilterState={changeFilterState}
            onSave={saveAudience}
            readOnly={isProtected}
          />
          <div className={styles.rightColHolder}>
            {audianceName ? (
              <div className={styles.results}>
                {filterState.hasUnsavedChanges && !filterState.isSaving && (
                  <p className={styles.watermark}>Update audience to refresh counts</p>
                )}
                {(filterState.isSaving || isLoading) && <LoadingIndicator />}
                {isProtected && (
                  <p className={styles.summaryDescription}>
                    This is a default audience and its filters cannot be changed.
                  </p>
                )}
                <div
                  className={styles.summary}
                  style={{
                    opacity:
                      filterState.isSaving || isLoading || filterState.hasUnsavedChanges
                        ? 0.3
                        : 1,
                    filter:
                      filterState.isSaving || isLoading || filterState.hasUnsavedChanges
                        ? 'blur(4px)'
                        : ''
                  }}
                >
                  <h2 className={styles.summaryTitle}>
                    {formatNumberAsThousands(totalSize)} unique guests
                  </h2>
                  {size > 10 && (
                    <p className={styles.summaryDescription}>
                      {formatNumberAsThousands(textSize)} to text &nbsp;•&nbsp;
                      {formatNumberAsThousands(emailSize)} to email
                    </p>
                  )}
                  <div className={styles.toggleSection}>
                    <ul>
                      <li>
                        <InfoIcon fill="#beafaf" />
                        Guests with reservations, contacted in 14 days and stayed within
                        last 7 days are not included.
                      </li>
                    </ul>
                  </div>
                </div>
                {deferredPreview && (
                  <div
                    style={{
                      opacity:
                        filterState.isSaving || isLoading || filterState.hasUnsavedChanges
                          ? 0.3
                          : 1,
                      filter:
                        filterState.isSaving || isLoading || filterState.hasUnsavedChanges
                          ? 'blur(4px)'
                          : '',
                      pointerEvents:
                        filterState.isSaving || isLoading || filterState.hasUnsavedChanges
                          ? 'none'
                          : 'auto'
                    }}
                  >
                    <Table widths={[200, 120, 250, 50]}>
                      <TitleRow>
                        <TitleCell>Name</TitleCell>
                        <TitleCell>Last checkout</TitleCell>
                        <TitleCell>From</TitleCell>
                        <TitleCell>
                          <span>
                            Stays in <br />
                            last 12 mo
                          </span>
                        </TitleCell>
                      </TitleRow>
                      {Object.values(deferredPreview).map(g => (
                        <Table.Row key={g.id}>
                          <Cell className={styles.cell}>
                            <>
                              <ReactTooltip />
                              <div data-tip={g.fullName} className={styles.nameCell}>
                                {g.fullName}
                              </div>
                            </>
                          </Cell>
                          <Cell className={styles.cell}>
                            {moment(g.lastCheckOut).format('MMM D, YYYY')}
                          </Cell>
                          <Cell className={styles.cell}>
                            {g.city},{g.state}
                          </Cell>
                          <Cell className={styles.cell}>{g.repeatInYearCount}</Cell>
                        </Table.Row>
                      ))}
                    </Table>
                  </div>
                )}
              </div>
            ) : (
              <div className={styles.resultsPlaceholder}>
                <div className={styles.resultsPlaceholderInner}>
                  <SearchIcon fill="#8A8A8A" />
                  <p>Do a search from left to start</p>
                </div>
              </div>
            )}
            <div className={styles.bottomBar}>
              {!isProtected && (
                <button
                  className={styles.deleteButton}
                  onClick={() => setIsDeletingConfVisible(true)}
                >
                  <DeleteIcon /> Delete audience
                </button>
              )}
              <Link to="/custom-campaigns">
                <button className={styles.secondaryCta}>Close</button>
              </Link>
              <button
                disabled={isCreating}
                onClick={createCampaign}
                className={styles.cta}
              >
                Create campaign
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return <LoadingIndicator />
}

export default Audience
